import {Component, Input, OnInit} from '@angular/core';
import {
  NavigationType,
  TaskRecordProcessNavigationDialogComponent
} from "../task-record-process-navigation-dialog/task-record-process-navigation-dialog.component";
import {StateName} from "../../../../../app.state-names";
import {TaskRecord, TaskRecordService} from "../../../../../lib/task/task-record.service";
import {UIRouter} from "@uirouter/angular";
import {MatDialog} from "@angular/material/dialog";
import {Set} from "immutable";
import {UiConstants} from "../../../../../util/core-utils";
import {ToasterService} from "../../../../../fork/angular2-toaster/src/toaster.service";
import {TranslateService} from "@ngx-translate/core";
import {QueryResult} from "../../../../../lib/util/services";
import {TaskRecordStateMachine} from "../../../../../lib/task/task-record-statemachine";
import {OperationRights} from "../../../../../app.right-definitions";
import {GrantedPermissionSetResolver, RightResolver} from "../../../../../lib/right.service";
import {TaskRecordRightModel} from "../../../../../util/task-record-utils";

@Component({
  selector: 'app-task-record-process-navigation',
  templateUrl: './task-record-process-navigation.component.html',
  styleUrls: ['./task-record-process-navigation.component.scss']
})
export class TaskRecordProcessNavigationComponent implements OnInit {

  @Input() taskRecord: TaskRecord.TaskRecord;

  constructor(
    private uiRouter: UIRouter,
    private dialog: MatDialog,
    private taskRecordService: TaskRecordService,
    private toasterService: ToasterService,
    private translateService: TranslateService
  ) {
  }

  ngOnInit(): void {
  }

  private generateRequest(): TaskRecord.QueryNextPrevTaskRecordRequest {
    return {
      currentTaskRecordId: this.taskRecord.taskRecordId,
      noProgressBar: true,
      parentDisabled: false,
      withFormRecord: false,
      fields: Set.of('id', 'task_id', 'name', 'external_id', 'task', 'state', 'creation_time'),
      rights: Set.of(OperationRights.TASK_RECORD_UPDATE)
    }
  }

  navigateToPrevTasks() {
    if (!this.taskRecord) {
      return;
    }
    const request = this.generateRequest();
    this.taskRecordService.getPreviousTaskRecordsInProcess(request).subscribe(taskRecords => {
      const handled: boolean = this.preHandleTaskRecordResult(taskRecords);
      if (handled) {
        return;
      }
      TaskRecordProcessNavigationDialogComponent.openDialog(this.dialog, {
        navigationType: NavigationType.PREV,
        currentTaskRecordId: this.taskRecord.taskRecordId!,
        taskRecords: taskRecords.items.toArray()
      }, result => {
        if (!result || !result.navigateTo) {
          return;
        }
        this.navigateTo(result.navigateTo);
      });
    });

  }

  navigateToNextTasks(cancelHandler?: () => void) {
    if (!this.taskRecord) {
      return;
    }
    const request = this.generateRequest();
    this.taskRecordService.getNextTaskRecordsInProcess(request).subscribe(taskRecords => {
      const handled: boolean = this.preHandleTaskRecordResult(taskRecords);
      if (handled) {
        return;
      }
      TaskRecordProcessNavigationDialogComponent.openDialog(this.dialog, {
        navigationType: NavigationType.NEXT,
        currentTaskRecordId: this.taskRecord.taskRecordId!,
        taskRecords: taskRecords.items.toArray()
      }, result => {
        if (!result || !result.navigateTo) {
          if (cancelHandler != undefined) {
            cancelHandler();
          }
          return;
        }
        this.navigateTo(result.navigateTo);
      });
    });
  }

  private preHandleTaskRecordResult(taskRecords: QueryResult<TaskRecord.TaskRecord>): boolean {
    if (taskRecords.items.size === 0) {
      this.toasterService.pop({
        timeout: UiConstants.ToastTimeoutLong,
        type: UiConstants.toastTypeError,
        title: this.translateService.instant('TASK_RECORD_PROCESS_NAVIGATION_EMPTY_TITLE'),
        body: this.translateService.instant('TASK_RECORD_PROCESS_NAVIGATION_EMPTY_BODY')
      });
      return true;
    }
    if (taskRecords.items.size === 1) {
      const taskRecord = taskRecords.items.get(0);
      this.navigateTo(taskRecord);
      return true;
    }
    return false;
  }

  private navigateTo(taskRecord: TaskRecord.TaskRecord) {
    const canEdit = TaskRecordStateMachine.StateMachine.canEdit(taskRecord.task, taskRecord.state);
    const rights = new TaskRecordRightModel(GrantedPermissionSetResolver.byGrantedRights(taskRecord.grantedRights));
    const hasRight = rights.update.hasRight();
    const navigateTo = this.mapNavigationId(taskRecord);
    if (hasRight && canEdit) {
      this.uiRouter.stateService.go(StateName.TASK_RECORD_EDIT, navigateTo);
      return;
    }
    this.uiRouter.stateService.go(StateName.TASK_RECORD_DETAIL, navigateTo);
  }

  private mapNavigationId(taskRecord: TaskRecord.TaskRecord): TaskRecordNavigationId {
    return {taskRecordId: taskRecord.taskRecordId, taskId: taskRecord.taskId};
  }

}

interface TaskRecordNavigationId {
  taskId: number;
  taskRecordId: number;
}
